import React from "react";
import { useState } from "react";
import {
  Coupon,
  CouponTable,
  fetchCouponByTableId,
  insertStudentInfo,
  markCouponUsed,
} from "../../api/firebase";
import { validateKorean } from "../../utils/index";
import AlertModal from "../modal/AlertModal";
import FormInput from "./FormInput";
import "./index.css";
import { useHistory } from "react-router-dom";
import store from "../../store";
import * as Sentry from "@sentry/react";
// import CouponCounter from "./CouponCounter";

export interface Course {
  id: CouponTable;
  title: string;
}

// init values
const studentStatusTypes = ["재학", "휴학", "졸업(미취업자)"];
const schoolTypes = ["대학교", "고등학교"];
export const courses: Course[] = [
  { id: "coupon-vue-start3", title: "Vue.js 시작하기 - Age of Vue.js" },
  { id: "coupon-vue3-start", title: "Vue 3 시작하기" },
  { id: "coupon-next-js", title: "Next.js 시작하기" },
  {
    id: "coupon-react-start",
    title: "만들면서 배우는 리액트: 컴포넌트 설계와 리팩토링",
  },
  { id: "coupon-vue-intermediate", title: "Vue.js 중급 강좌" },
  { id: "coupon-vue3-intermediate", title: "Vue 3 중급 - Composition API" },
  { id: "coupon-webpack", title: "프론트엔드 개발자를 위한 웹팩" },
  { id: "coupon-vue-advanced", title: "Vue.js 완벽 가이드" },
  {
    id: "coupon-vue-endgame",
    title: "Vue.js 끝장내기 - 실무에 필요한 모든 것",
  },
  { id: "coupon-ts12", title: "타입스크립트 입문" },
  { id: "coupon-ts22", title: "실전 프로젝트로 배우는 타입스크립트" },
  { id: "coupon-testing-start", title: "실전 프론트엔드 테스팅 시작하기" },
  { id: "coupon-vue-ts", title: "Vue.js + TypeScript 완벽 가이드" },
  {
    id: "coupon-react-vue-ts",
    title: "React와 Vue로 배우는 TypeScript 필수 개념",
  },
  { id: "coupon-pwa", title: "PWA 시작하기" },
];
const INIT_FILE = { fileName: "", fileType: "" };
const FILE_MAXIMUM_ALLOWED_SIZE = 2097152;

function ApplicationForm() {
  // form values
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [birth, setBirth] = useState("");
  const [studentStatus, setStudentStatus] = useState(studentStatusTypes[0]);
  const [schoolName, setSchoolName] = useState("");
  const [schoolType, setSchoolType] = useState(schoolTypes[0]);
  const [file, setFile] = useState(INIT_FILE);
  const [isFileValid, setFileValid] = useState(false);
  const [course, setCourse] = useState(courses[0]);
  const [idCardVerified, setIdCardVerification] = useState(false);
  // modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("에러");
  const [modalContents, setModalContents] = useState(
    "필수 항목을 작성해 주세요",
  );
  // router
  const history = useHistory();

  function loggingFormValues() {
    const formInputs = {
      email,
      name,
      birth,
      studentStatus,
      schoolName,
      file,
      course,
      idCardVerified,
    };
    Sentry.captureMessage("[DEBUG] " + JSON.stringify(formInputs));
  }

  async function submitForm(event: React.FormEvent) {
    event.preventDefault();
    loggingFormValues();
    if (!isFormValid()) {
      openModal("입력 양식 에러", `신청 항목을 모두 입력해주세요`);
      return;
    }
    // TODO: 인증 완료 화면에 뷰, 웹팩, 타입스크립트 교안, 내 블로그 주소 안내해 보기
    try {
      const trimedEmail = email.trim();
      const trimedName = name.trim();
      await insertStudentInfo({
        email: trimedEmail,
        birth: birth.trim(),
        name: trimedName,
        school: `${schoolName.trim()}${schoolType}`,
        studentStatus,
        file,
        verified: idCardVerified,
        currentTime: new Date().getTime(),
        insertedAt: new Date().toLocaleString(),
      });
      store.setCourse(course);
      store.setSubmitted();
      // TODO: 인증이 완료되었을 때만 쿠폰을 하나 발급해서 다음 화면으로 넘겨주도록 로직 구현 필요. 이유: 인증 완료 페이지의 가드 전에 useEffect를 돌려서 쿠폰을 받아오게 되어 있음
      const querySnapshot = await fetchCouponByTableId(course.id);
      querySnapshot.forEach(async (d) => {
        const coupon = d.data() as Coupon;
        const docId = coupon.id;
        const couponCode = coupon["코드"];
        store.setCouponCode(couponCode);
        await markCouponUsed({
          docId,
          student: trimedName,
          email: trimedEmail,
          course,
        });
      });
      // requestEmailSender({
      //   studentEmail: trimedEmail,
      //   studentName: trimedName,
      //   couponLink: store.getCourseLink(),
      // });
      // TODO: 신청한 학생 정보(이메일)에 어떤 쿠폰이 발급되었는지 DB로 값 관리하기
      history.push("/complete");
    } catch (error) {
      openModal("서버 에러", `서버 에러입니다 잠시 후 다시 시도해주세요.`);
    }
  }

  function isFormValid(): boolean {
    const isEmailValid = email.trim().length !== 0;
    const isNameValid = name.trim().length !== 0 && validateKorean(name);
    const isSchoolValid =
      schoolName.trim().length !== 0 && validateKorean(schoolName);
    return (
      isEmailValid &&
      isNameValid &&
      isSchoolValid &&
      isFileValid &&
      idCardVerified
    );
  }

  function openModal(title: string, contents: string) {
    setModalTitle(title);
    setModalContents(contents);
    setModalOpen(true);
  }

  async function handleFile(event: React.ChangeEvent<HTMLInputElement>) {
    setFileValid(false);
    if (!event.target.files?.length) {
      return;
    }
    if (!name.length || !schoolName.length) {
      openModal("학생증 인증 에러", `이름과 학교명을 먼저 적어주세요`);
      setFile(INIT_FILE);
      event.target.value = "";
      return;
    }
    const selectedFile = event.target.files[0];
    const validFileTypes = ["image/png", "image/jpg", "image/jpeg"];
    const isFileTypeInvalid =
      validFileTypes.findIndex((type) => type === selectedFile.type) === -1;
    if (isFileTypeInvalid) {
      openModal("학생증 에러", `파일 형식이 맞지 않습니다`);
      setFile(INIT_FILE);
      event.target.value = "";
      return;
    }
    const isFileSizeTooBig = selectedFile.size >= FILE_MAXIMUM_ALLOWED_SIZE;
    if (isFileSizeTooBig) {
      openModal("학생증 에러", `파일 크기가 2MB를 초과하였습니다`);
      setFile(INIT_FILE);
      event.target.value = "";
      return;
    }
    try {
      // NOTE: 학생증 인증 로직 제거
      // const {
      //   data: { result },
      // } = await verifyIdCard({ image: selectedFile });
      // // TODO: 학생증 인증 로직 구현 필요
      // if (!validateStudentCard(result, name, schoolType)) {
      //   openModal(
      //     "학생증 에러",
      //     `학생증 인증에 실패하였습니다. 뚜렷하게 촬영해서 다시 인증해주세요`,
      //   );
      //   setFile(INIT_FILE);
      //   event.target.value = "";
      //   return;
      // }
      setFile({ fileName: selectedFile.name, fileType: selectedFile.type });
      setFileValid(true);
      setIdCardVerification(true);
    } catch (error) {
      Sentry.captureMessage("[DEBUG] " + JSON.stringify(error));
      event.target.value = "";
      setFile(INIT_FILE);
      setFileValid(false);
      setIdCardVerification(false);
      if (!error) {
        openModal("네트워크 에러", `네트워크 연결을 확인해주세요`);
        return;
      }
      const sizeErrorRegex = /byte/g;
      const widthHeightErrorRegex = /pixel/g;
      if (
        error.response.data.code === -602 &&
        sizeErrorRegex.test(error.response.data.msg)
      ) {
        openModal("학생증 에러", `학생증 크기가 2MB를 초과하였습니다`);
      }
      if (
        error.response.data.code === -602 &&
        widthHeightErrorRegex.test(error.response.data.msg)
      ) {
        openModal(
          "학생증 에러",
          `학생증 가로 세로 길이가 2048px 이하여야 합니다`,
        );
      }
    }
  }

  function selectCourse(event: any) {
    const selected = courses.find((c) => c.title === event.target.value);
    setCourse(selected!);
  }

  return (
    <div className="container">
      <form onSubmit={(event) => submitForm(event)}>
        <FormInput
          input={email}
          inputId="email"
          inputLabel="인프런 계정(이메일)"
          inputPlaceholder="jangkeehyo@gmail.com"
          onInput={(event) =>
            setEmail((event.target as HTMLInputElement).value)
          }
        ></FormInput>
        <FormInput
          input={name}
          inputId="name"
          inputLabel="이름(실명)"
          inputPlaceholder="장기효"
          onInput={(event) => setName((event.target as HTMLInputElement).value)}
        ></FormInput>
        <div className="form-box pb-4 flex">
          <div className="form-wrapper flex-1 mr-2">
            <label htmlFor="birth" className="text-small font-medium pb-1">
              출생년도
            </label>
            <input
              id="birth"
              type="text"
              className="border-gray rounded"
              placeholder="1990"
              value={birth}
              onInput={(event) =>
                setBirth((event.target as HTMLInputElement).value)
              }
            />
          </div>
          <div className="form-wrapper flex-1">
            <label
              htmlFor="student-status"
              className="text-small font-medium pb-1"
            >
              재학 상태
            </label>
            <select
              id="student-status"
              className="rounded border-gray"
              value={studentStatus}
              onChange={(event) => setStudentStatus(event.target.value)}
            >
              {studentStatusTypes.map((statusType, index) => (
                <option key={index}>{statusType}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="form-box pb-4">
          <label htmlFor="school-name" className="text-small font-medium pb-1">
            학교명
          </label>
          <div className="flex">
            <div className="form-wrapper flex-1 mr-2">
              <input
                id="school-name"
                type="text"
                className="school-name border-gray rounded"
                value={schoolName}
                placeholder="제주"
                onInput={(event) =>
                  setSchoolName((event.target as HTMLInputElement).value)
                }
              />
            </div>
            <div className="form-wrapper flex-1">
              <select
                className="school rounded border-gray"
                value={schoolType}
                onChange={(event) => setSchoolType(event.target.value)}
              >
                {schoolTypes.map((schoolType, index) => (
                  <option key={index}>{schoolType}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="form-box pb-4">
          <label className="pb-1" htmlFor="id-card">
            학생증
          </label>
          <input
            id="id-card"
            type="file"
            className="pb-1"
            onChange={(event) => handleFile(event)}
            accept=".png, .jpg, .jpeg"
          />
          <p className="text-xsmall font-gray pb-3">
            이미지는 가로 세로 2048px 이하이며 최대 2MB의 PNG, JPG, JPEG만
            지원합니다.
          </p>
        </div>
        <div className="form-box pb-4">
          <label className="pb-1" htmlFor="course">
            강의명
          </label>
          <select
            id="course"
            className="px-3 py-2 rounded border-gray"
            value={course.title}
            onChange={selectCourse}
          >
            {courses.map((course, index) => (
              <option key={index}>{course.title}</option>
            ))}
          </select>
        </div>
        <div className="form-box flex flex-end py-2">
          <button
            className="btn rounded py-2 px-4 font-medium text-small bg-indigo"
            type="submit"
          >
            쿠폰 신청
          </button>
        </div>
      </form>
      {/* <CouponCounter></CouponCounter> */}
      <div className="pb-2"></div>
      <AlertModal
        open={modalOpen}
        title={modalTitle}
        contents={modalContents}
        onClick={() => setModalOpen(false)}
      ></AlertModal>
    </div>
  );
}

export default ApplicationForm;
